/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { GateSpec, TableInfoSpec } from "../../../shared/contracts";
import { GateType } from "../../core/enums";
import { ItemData, toVertex2D } from "../../core/item-data";
import type { Vertex2D } from "../../math/vertex2d";

export class GateData extends ItemData {
  /** Radians. */
  public angleMax: number = Math.PI / 2.0;
  /** Radians. */
  public angleMin: number = 0;
  public damping: number = 0.985;
  public elasticity: number = 0.3;
  public friction: number = 0.02;
  public gateType: number = GateType.GateWireW;
  public gravityFactor: number = 0.25;
  public height: number = 50;
  public isCollidable: boolean = true;
  public isVisible: boolean = true;
  public length: number = 100;
  /** Degrees. */
  public rotation: number = -90;
  public showBracket: boolean = true;
  public szMaterial?: string;
  public szSurface?: string;
  public twoWay: boolean = false;
  public center: Vertex2D;

  public constructor(itemName: string, center: Vertex2D) {
    super(itemName);
    this.center = center;
  }

  /** Builds the data from a table spec. A field that the spec leaves out keeps its upstream default. */
  public static fromSpec(spec: GateSpec, _info?: TableInfoSpec): GateData {
    const data = new GateData(spec.name, toVertex2D(spec.center));
    data.length = spec.length;
    data.height = spec.height;
    data.rotation = spec.rotation;
    data.angleMin = spec.angleMin ?? data.angleMin;
    data.angleMax = spec.angleMax ?? data.angleMax;
    data.damping = spec.damping ?? data.damping;
    data.elasticity = spec.elasticity ?? data.elasticity;
    data.friction = spec.friction ?? data.friction;
    data.gravityFactor = spec.gravityFactor ?? data.gravityFactor;
    data.gateType = spec.gateType ?? data.gateType;
    data.twoWay = spec.twoWay ?? data.twoWay;
    data.showBracket = spec.showBracket ?? data.showBracket;
    data.isCollidable = spec.isCollidable ?? true;
    data.isVisible = spec.isVisible ?? true;
    data.szMaterial = spec.szMaterial;
    data.szSurface = spec.szSurface;
    return data;
  }
}
